// Draw-a-route (F43): floating panel over the main map — desktop: popover
// under the toolbar (like FilterSheet); mobile: bottom sheet with no backdrop
// so the map above stays tappable. While the panel is open MapPage puts the
// main map in draw mode, so points are added on the map the user was already
// looking at instead of a separate full-screen page.
//
// Submit is a follow-up-mutation chain, not a nested insert: importTrack →
// createRoute → createRouteTrack (MAIN) → createRoutePlace per linked place.
// A failure mid-chain shows the error and does not retry — an already-imported
// track is simply orphaned, which is acceptable (there is no working
// deleteTrack, and orphan tracks are unlisted).
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Trash2, Undo2, X } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  activitiesToWire,
  CREATE_ROUTE,
  CREATE_ROUTE_PLACE,
  CREATE_ROUTE_TRACK,
  IMPORT_TRACK,
  type CreateRouteResult,
  type ImportTrackResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import { parseGpx, type ParsedGpx } from "../lib/gpx";
import { ACTIVITIES, ACTIVITY_ICONS, formatDistance, type Activity } from "../lib/format";
import { useT } from "../lib/i18n";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { LoginGate } from "./LoginGate";
import { PlacePicker, type PickedPlace } from "./PlacePicker";
import { Chip, Field, TextInput } from "./ui";
import { MiniMap } from "../map/MiniMap";
import { toFeature, type Geometry } from "../lib/geo";
import type { DrawState } from "../map/MapView";

type Props = {
  draw: DrawState;
  onChange: (next: DrawState) => void;
  onClose: () => void;
};

function haversineM(a: [number, number], b: [number, number]): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

function drawnDistanceM(points: [number, number][]): number {
  let d = 0;
  for (let i = 1; i < points.length; i++) d += haversineM(points[i - 1]!, points[i]!);
  return d;
}

export function DrawRoutePanel({ draw, onChange, onClose }: Props) {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();

  const [mode, setMode] = useState<"draw" | "upload">("draw");
  const [gpx, setGpx] = useState<{ parsed: ParsedGpx; raw: string; fileName: string } | null>(null);
  const [gpxError, setGpxError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [activities, setActivities] = useState<Activity[]>([]);
  const [duration, setDuration] = useState("");
  const [places, setPlaces] = useState<PickedPlace[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Refs mirror draw state for the window keydown handler (registered once).
  const stateRef = useRef({ mode, draw, onChange });
  stateRef.current = { mode, draw, onChange };

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const { mode: m, draw: d, onChange: change } = stateRef.current;
      if (m !== "draw") return;
      // Don't hijack keys while the user is typing in the form.
      if ((e.target as HTMLElement | null)?.closest?.("input, textarea, select")) return;
      if ((e.key === "Delete" || e.key === "Backspace") && d.selected != null) {
        e.preventDefault();
        change({ points: d.points.filter((_, i) => i !== d.selected), selected: null });
      } else if (e.key.toLowerCase() === "z" && (e.ctrlKey || e.metaKey) && d.points.length) {
        e.preventDefault();
        change({ points: d.points.slice(0, -1), selected: null });
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  async function onGpxSelect(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const raw = await file.text();
      setGpx({ parsed: parseGpx(raw), raw, fileName: file.name });
      setGpxError(null);
    } catch (err) {
      setGpx(null);
      setGpxError(err instanceof Error ? err.message : String(err));
    }
  }

  const gpxGeometry = useMemo<Geometry | null>(
    () => (gpx ? (JSON.parse(gpx.parsed.geojson) as Geometry) : null),
    [gpx],
  );

  const points = draw.points;
  const distanceM = mode === "draw" ? drawnDistanceM(points) : (gpx?.parsed.distanceM ?? 0);
  const hasGeometry = mode === "draw" ? points.length >= 2 : gpx != null;

  const create = useMutation({
    mutationFn: async () => {
      // Drawn vertices have no elevation — Z=0 throughout, so ascent/descent
      // come out as 0. Elevation data comes from GPX uploads.
      const geojson =
        mode === "draw"
          ? JSON.stringify({ type: "LineString", coordinates: points.map(([lng, lat]) => [lng, lat, 0]) })
          : gpx!.parsed.geojson;
      const imported = await gqlRequest<ImportTrackResult>(IMPORT_TRACK, {
        geojson,
        pointTimes: mode === "upload" ? gpx!.parsed.pointTimes : null,
        sourceGpx: mode === "upload" ? gpx!.raw : null,
      });
      const created = await gqlRequest<CreateRouteResult>(CREATE_ROUTE, {
        input: {
          name: name.trim(),
          // Lowercase labels — see the TODO(pdbq) enum-array note in queries.ts.
          activities: activitiesToWire(activities),
          durationMin: duration.trim() === "" ? null : Math.round(Number(duration)),
          // RLS checks this against the JWT claim; nothing defaults it.
          createdBy: personId,
        },
      });
      const routeId = created.createRoute.route.id;
      await gqlRequest(CREATE_ROUTE_TRACK, { routeId, trackId: imported.importTrack.result.id, sort: 0 });
      for (const p of places) {
        await gqlRequest(CREATE_ROUTE_PLACE, { routeId, placeId: p.id });
      }
      return routeId;
    },
    onSuccess: (routeId) => navigate(`/route/${routeId}`),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = t("newRoute.errName");
    if (activities.length === 0) errors.activities = t("form.pickActivity");
    if (!hasGeometry) errors.geometry = mode === "draw" ? t("newRoute.errGeomDraw") : t("newRoute.errGeomUpload");
    if (duration && (!Number.isFinite(Number(duration)) || Number(duration) <= 0))
      errors.duration = t("newRoute.errDuration");
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0 && !create.isPending) create.mutate();
  }

  const tabClass = (active: boolean) =>
    `flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
      active
        ? "bg-primary text-primary-foreground shadow-card"
        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
    }`;

  return (
    // No mobile backdrop (unlike FilterSheet): the map above the sheet must
    // stay tappable so points can be added while the form is open.
    <div className="fixed inset-x-0 bottom-16 z-40 sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-3 sm:top-14 sm:w-96">
      <div className="max-h-[45vh] overflow-y-auto rounded-t-2xl border-t bg-card text-card-foreground p-5 shadow-pop sm:max-h-[70vh] sm:rounded-xl sm:border">
        {/* Drag affordance, mobile sheet only */}
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-muted sm:hidden" />
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight text-foreground">{t("newRoute.title")}</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label={t("common.cancel")}
            className="text-muted-foreground hover:text-foreground"
          >
            <X aria-hidden className="size-4" />
          </Button>
        </div>

        <LoginGate action="gateAction.drawRoute" next="/">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="flex gap-1 rounded-xl border bg-card p-1 shadow-card" role="tablist">
              <button type="button" role="tab" aria-selected={mode === "draw"} onClick={() => setMode("draw")} className={tabClass(mode === "draw")}>
                {t("newRoute.drawTab")}
              </button>
              <button type="button" role="tab" aria-selected={mode === "upload"} onClick={() => setMode("upload")} className={tabClass(mode === "upload")}>
                {t("newRoute.uploadTab")}
              </button>
            </div>

            {mode === "draw" ? (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium tabular-nums text-foreground">
                    {t(points.length === 1 ? "newRoute.pointsOne" : "newRoute.pointsOther", { count: points.length })} · {formatDistance(distanceM)}
                  </span>
                  <div className="flex gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={() => onChange({ points: points.slice(0, -1), selected: null })}
                      disabled={points.length === 0}
                      title={t("newRoute.undoTitle")}
                    >
                      <Undo2 aria-hidden className="size-3.5" />
                      {t("newRoute.undo")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={() => onChange({ points: [], selected: null })}
                      disabled={points.length === 0}
                    >
                      <Trash2 aria-hidden className="size-3.5" />
                      {t("newRoute.clear")}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{t("newRoute.drawHelp")}</p>
                <p className="text-xs text-muted-foreground">{t("newRoute.noElevNote")}</p>
              </>
            ) : (
              <>
                <Field label={t("newRoute.gpxFile")} error={gpxError ?? undefined}>
                  {gpx ? (
                    <div className="flex items-center justify-between rounded-xl border border-primary/25 bg-primary/5 px-3 py-2">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground">{gpx.fileName}</span>
                        <span className="block text-xs text-muted-foreground">
                          {t("gpx.stats", { count: gpx.parsed.pointCount, distance: formatDistance(gpx.parsed.distanceM) })}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setGpx(null)}
                        className="ml-2 shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                        aria-label={t("gpx.removeAria")}
                      >
                        <X aria-hidden className="size-4" />
                      </button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept=".gpx"
                      onChange={onGpxSelect}
                      className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/15"
                    />
                  )}
                </Field>
                {gpxGeometry ? (
                  <MiniMap
                    features={[toFeature(gpxGeometry, { id: "gpx-preview" })]}
                    className="h-40 w-full overflow-hidden rounded-xl border shadow-card"
                  />
                ) : (
                  <p className="text-xs text-muted-foreground">{t("newRoute.uploadHint")}</p>
                )}
              </>
            )}
            {fieldErrors.geometry && <p className="text-sm text-destructive">{fieldErrors.geometry}</p>}

            <Field label={t("common.name")} error={fieldErrors.name}>
              <TextInput value={name} placeholder={t("newRoute.namePlaceholder")} onChange={(e) => setName(e.target.value)} />
            </Field>

            <Field label={t("form.activities")} error={fieldErrors.activities}>
              <div className="flex flex-wrap gap-2">
                {ACTIVITIES.map((a) => {
                  const Icon = ACTIVITY_ICONS[a];
                  const active = activities.includes(a);
                  return (
                    <Chip
                      key={a}
                      active={active}
                      onClick={() => setActivities(active ? activities.filter((x) => x !== a) : [...activities, a])}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <Icon aria-hidden className="size-3.5" />
                        {labelizeT(a)}
                      </span>
                    </Chip>
                  );
                })}
              </div>
            </Field>

            <Field label={t("form.durationOptional")} error={fieldErrors.duration}>
              <TextInput
                type="number"
                inputMode="numeric"
                min={1}
                value={duration}
                placeholder="e.g. 240"
                onChange={(e) => setDuration(e.target.value)}
              />
            </Field>

            <Field label={t("newRoute.linkedPlaces")}>
              <PlacePicker selected={places} onChange={setPlaces} />
            </Field>

            {create.error != null && (
              <Alert variant="destructive">
                <AlertDescription>
                  {create.error instanceof Error ? create.error.message : String(create.error)}
                </AlertDescription>
              </Alert>
            )}

            <Button type="submit" disabled={create.isPending} className="w-full">
              {create.isPending ? t("newRoute.busy") : t("newRoute.submit")}
            </Button>
          </form>
        </LoginGate>
      </div>
    </div>
  );
}
